import React, { useState, useEffect, useCallback, useContext } from 'react';
import { api } from './api/api';
import { AuthContext } from '../App';

const phaColorsFor = (p, h, a) => {
  if (p == null || h == null || a == null) return {};
  const ranked = [
    { key: 'power', v: p }, { key: 'handling', v: h }, { key: 'acceleration', v: a },
  ].sort((x, y) => x.v - y.v);
  return { [ranked[0].key]: '#FF00FF', [ranked[1].key]: '#4472C4' };
};

const calendarRankColor = (rank) => {
  if (rank == null) return undefined;
  const t = Math.max(0, Math.min(1, (rank - 1) / 16));
  return `hsl(${120 - 120 * t}, 65%, 40%)`;
};

const EASY_HARD_LABELS = { 1: 'Very Easy', 2: 'Easy', 3: 'Medium', 4: 'Hard', 5: 'Very Hard' };
const LOW_HIGH_LABELS = { 1: 'Very Low', 2: 'Low', 3: 'Medium', 4: 'High', 5: 'Very High' };
const ENERGY_LABELS = { 0: 'Extremely Low', ...LOW_HIGH_LABELS };

const rating = (v, labels) => v == null ? '—' : (labels[v] ?? v);

// 1 (green) -> 5 (red); 0 (Energy's "Extremely Low") is a special-cased purple,
// off that scale rather than an extension of it.
const ratingColor = (v) => {
  if (v == null) return undefined;
  if (v === 0) return '#9333ea';
  const t = Math.max(0, Math.min(1, (v - 1) / 4));
  return `hsl(${120 - 120 * t}, 65%, 40%)`;
};

const SeasonTable = ({ title, season, rows, loading, error }) => (
  <div className="fb-card">
    <div className="fb-card-header">
      <span className="fb-card-title">{title}{season ? ` (S${season})` : ''}</span>
    </div>
    <div className="fb-table-wrap">
      {loading ? (
        <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>
      ) : error ? (
        <div style={{ padding: 16, color: 'var(--c-error)', fontSize: 13 }}>{error}</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>
          Calendar not published yet for this season.
        </div>
      ) : (
        <>
          {rows.length < 17 && (
            <div style={{ padding: '8px 16px', color: 'var(--c-text-muted)', fontSize: 12 }}>
              Showing {rows.length}/17 tracks — a new calendar track may be missing from the database.
            </div>
          )}
          <table className="fb-table fb-table-sm ra-table" style={{ whiteSpace: 'nowrap' }}>
            <thead>
              <tr>
                <th>#</th><th>Track</th><th>P</th><th>H</th><th>A</th>
                <th>Overtaking</th><th>Tyre Wear</th><th>Grip</th><th>Laps</th><th>Energy</th>
                <th>Lap Gain @100CT</th><th>Race Gain @100CT</th>
                <th>Race Gain Calendar</th><th>Race Gain Rank</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t, i) => {
                const colors = phaColorsFor(t.phaOffsetPower, t.phaOffsetHandling, t.phaOffsetAcceleration);
                return (
                  <tr key={t.trackId ?? i}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{t.trackName}</td>
                    <td style={{ color: colors.power }}>{t.phaOffsetPower ?? '—'}</td>
                    <td style={{ color: colors.handling }}>{t.phaOffsetHandling ?? '—'}</td>
                    <td style={{ color: colors.acceleration }}>{t.phaOffsetAcceleration ?? '—'}</td>
                    <td style={{ color: ratingColor(t.overtaking), fontWeight: 600 }}>{rating(t.overtaking, EASY_HARD_LABELS)}</td>
                    <td style={{ color: ratingColor(t.tyres), fontWeight: 600 }}>{rating(t.tyres, EASY_HARD_LABELS)}</td>
                    <td style={{ color: ratingColor(t.grip), fontWeight: 600 }}>{rating(t.grip, LOW_HIGH_LABELS)}</td>
                    <td>{t.laps ?? '—'}</td>
                    <td style={{ color: ratingColor(t.energyRating), fontWeight: 600 }}>{rating(t.energyRating, ENERGY_LABELS)}</td>
                    <td>{t.lapGain100Ct ?? '—'}</td>
                    <td>{t.raceGain100Ct ?? '—'}</td>
                    <td style={{ color: calendarRankColor(t.raceGainRankCalendar), fontWeight: 700 }}>
                      {t.raceGainRankCalendar ?? '—'}
                    </td>
                    <td>{t.raceGainRankGlobal ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  </div>
);

const SeasonGains = () => {
  const { userRole } = useContext(AuthContext);
  const canForceUpdate = userRole === 'ADMIN' || userRole === 'UPDATER';

  // Backend resolves "current"/"next" itself — the frontend doesn't need to
  // know or compute the season number for this.
  const [currentSeasonNum, setCurrentSeasonNum] = useState(null);
  const [nextSeasonNum, setNextSeasonNum] = useState(null);
  const [currentRows, setCurrentRows] = useState([]);
  const [nextRows, setNextRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);

  const loadTables = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // `season=current`/`next` resolve against the server's last-known
      // season; on a fresh backend restart that's unset and both calls fall
      // back to "no season" (all 64 tracks, no calendar rank). Warm it up
      // first so that can't happen.
      await api.get('/update/today').catch(() => {});
      const [curRes, nextRes] = await Promise.all([
        api.get('/track/gains?season=current'),
        api.get('/track/gains?season=next'),
      ]);
      const curRows = curRes.data || [];
      const nextRows_ = nextRes.data || [];
      setCurrentRows(curRows);
      setNextRows(nextRows_);
      // Optional: backend may include a `season` number on each row for display.
      setCurrentSeasonNum(curRows[0]?.season ?? null);
      setNextSeasonNum(nextRows_[0]?.season ?? null);
    } catch (err) {
      setError(err.response?.status === 403 ? 'Access denied.' : 'Failed to load season gains.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTables(); }, [loadTables]);

  const handleForceUpdate = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      await api.get('/update/season-start');
      await loadTables();
      setRefreshMsg('Updated.');
    } catch (err) {
      setRefreshMsg(err.response?.status === 403 ? 'Access denied.' : 'Update failed.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      <div className="fb-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="fb-page-title">PHA / Gains by Season</div>
          <div className="fb-page-sub">Per-track PHA offsets and Lap/Race Gain, current + next season</div>
        </div>
        {canForceUpdate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {refreshMsg && <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{refreshMsg}</span>}
            <button type="button" className="fb-btn fb-btn-secondary fb-btn-sm" disabled={refreshing} onClick={handleForceUpdate}>
              {refreshing ? 'Updating…' : 'Force Update'}
            </button>
          </div>
        )}
      </div>

      {error && !loading && currentRows.length === 0 && nextRows.length === 0 ? (
        <div style={{ color: 'var(--c-error)', fontSize: 14 }}>{error}</div>
      ) : (
        <>
          <SeasonTable title="Current Season" season={currentSeasonNum} rows={currentRows} loading={loading} error={null} />
          <SeasonTable title="Next Season" season={nextSeasonNum} rows={nextRows} loading={loading} error={null} />
        </>
      )}
    </>
  );
};

export default SeasonGains;
